import { Preferences } from "@capacitor/preferences";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { isNativePlatform, STORAGE_KEYS } from "./storage";
import type { ProductDataMultimedia, ProductTypeMultimedia } from "@/pages/Workshop/MultimediaCard";

export type DownloadStatus =
	| "pending"
	| "downloading"
	| "completed"
	| "failed"
	| "paused";

export interface OfflineVideoMetadata {
	videoId: string; // unique ID combining workshopId and courseClassesId
	// workshopId: string;
	courseClassesId: string;
	productData: ProductDataMultimedia;
	videoName: string;
	duration: number;

	// Video source info
	videoSource: "vimeo_source" | "akademia_source";
	vimeoId?: number;
	originalVideoUrl?: string;

	// Download info
	selectedResolution: string; // e.g., "720p", "1080p"
	downloadUrl: string;
	fileSizeMB: number;

	// File info
	localFilePath: string; // Path where video is stored on device
	fileName: string;

	// Status
	status: DownloadStatus;
	downloadProgress: number; // 0-100

	// Timestamps
	downloadStartedAt?: number;
	downloadCompletedAt?: number;
	lastAccessedAt?: number;
}

export interface OfflineVideosStorage {
	[videoId: string]: OfflineVideoMetadata;
}

/**
 * Generate unique video ID from workshopId and courseClassesId and ProductTypeMultimedia
 */
export const generateVideoId = (
	productType: ProductTypeMultimedia,
	workshopId: string,
	courseClassesId: string
): string => {
	return `${productType}_${workshopId}_${courseClassesId}`;
};

/**
 * Get offline videos metadata from storage
 */
export const getOfflineVideos = async (): Promise<OfflineVideosStorage> => {
	try {
		if (isNativePlatform()) {
			const { value } = await Preferences.get({
				key: STORAGE_KEYS.OFFLINE_VIDEOS,
			});
			if (value) {
				try {
					return JSON.parse(value) as OfflineVideosStorage;
				} catch (error) {
					console.error("Error parsing offline videos:", error);
					return {};
				}
			}
			return {};
		} else {
			const value = localStorage.getItem(STORAGE_KEYS.OFFLINE_VIDEOS);
			if (value) {
				try {
					return JSON.parse(value) as OfflineVideosStorage;
				} catch (error) {
					console.error("Error parsing offline videos:", error);
					return {};
				}
			}
			return {};
		}
	} catch (error) {
		console.error("Error getting offline videos:", error);
		return {};
	}
};

/**
 * Set offline videos metadata in storage
 */
export const setOfflineVideos = async (
	videos: OfflineVideosStorage
): Promise<void> => {
	try {
		const value = JSON.stringify(videos);
		if (isNativePlatform()) {
			await Preferences.set({
				key: STORAGE_KEYS.OFFLINE_VIDEOS,
				value,
			});
		} else {
			localStorage.setItem(STORAGE_KEYS.OFFLINE_VIDEOS, value);
		}
	} catch (error) {
		console.error("Error setting offline videos:", error);
	}
};

/**
 * Add or update a single offline video metadata
 */
export const upsertOfflineVideo = async (
	video: OfflineVideoMetadata
): Promise<void> => {
	const videos = await getOfflineVideos();
	videos[video.videoId] = video;
	await setOfflineVideos(videos);
};

/**
 * Get a single offline video metadata
 */
export const getOfflineVideo = async (
	videoId: string
): Promise<OfflineVideoMetadata | null> => {
	const videos = await getOfflineVideos();
	return videos[videoId] || null;
};

/**
 * Remove a single offline video (metadata and file)
 */
export const removeOfflineVideo = async (videoId: string): Promise<void> => {
	try {
		const videos = await getOfflineVideos();
		const video = videos[videoId];

		if (video && video.localFilePath && isNativePlatform()) {
			// Delete the actual video file from device
			try {
				await Filesystem.deleteFile({
					path: video.localFilePath,
					directory: Directory.Data,
				});
				console.log(`🗑️ Deleted video file: ${video.localFilePath}`);
			} catch (error) {
				console.error("Error deleting video file:", error);
			}
		}

		// Remove from metadata
		delete videos[videoId];
		await setOfflineVideos(videos);
	} catch (error) {
		console.error("Error removing offline video:", error);
		throw error;
	}
};

/**
 * Update download progress for a video
 */
export const updateDownloadProgress = async (
	videoId: string,
	progress: number,
	status: DownloadStatus
): Promise<void> => {
	const videos = await getOfflineVideos();
	if (videos[videoId]) {
		videos[videoId].downloadProgress = progress;
		videos[videoId].status = status;

		if (status === "completed") {
			videos[videoId].downloadCompletedAt = Date.now();
		}

		await setOfflineVideos(videos);
	}
};

/**
 * Update last accessed time for a video
 */
export const updateLastAccessed = async (videoId: string): Promise<void> => {
	const videos = await getOfflineVideos();
	if (videos[videoId]) {
		videos[videoId].lastAccessedAt = Date.now();
		await setOfflineVideos(videos);
	}
};

/**
 * Get total storage used by offline videos
 */
export const getTotalStorageUsedMB = async (): Promise<number> => {
	const videos = await getOfflineVideos();
	return Object.values(videos).reduce((total, video) => {
		return total + (video.status === "completed" ? video.fileSizeMB : 0);
	}, 0);
};

/**
 * Check if a video is already downloaded
 */
// export const isVideoDownloaded = async (
// 	productType: ProductTypeMultimedia,
// 	workshopId: string,
// 	courseClassesId: string
// ): Promise<boolean> => {
// 	const videoId = generateVideoId(productType, workshopId, courseClassesId);
// 	const video = await getOfflineVideo(videoId);
// 	return video !== null && video.status === "completed";
// };
